import { useState } from "react";
import { useNavigate } from "react-router-dom";
import heroBg from "@/assets/hero-bg.webp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Search, MapPin, Car } from "lucide-react";

const HeroSearch = () => {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/search?q=${encodeURIComponent(query.trim())}`);
    }
  };

  const handleUseLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          navigate(`/search?lat=${pos.coords.latitude}&lng=${pos.coords.longitude}`);
        },
        () => {
          alert("Unable to get your location. Please enter a city or zip code.");
        }
      );
    }
  };

  return (
    <section className="relative overflow-hidden py-20 md:py-32">
      <img
        src={heroBg}
        alt=""
        aria-hidden="true"
        fetchPriority="high"
        decoding="async"
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-background/65" />
      <div className="container mx-auto px-4 text-center relative z-10">
        <h1 className="text-4xl md:text-6xl font-bold tracking-tight mb-4">
          Find the <span className="text-primary">Best Price</span> for Your Ride
        </h1>
        <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
          Find local drivers and transportation companies. Airport transfers, private drivers, and car services with direct contact and no hidden fees.
        </p>

        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto mb-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input
              placeholder="Enter city, state, or zip code..."
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="pl-10 h-12 text-base"
            />
          </div>
          <Button type="submit" size="lg" className="h-12">
            Search
          </Button>
        </form>

        <Button variant="ghost" onClick={handleUseLocation} className="text-muted-foreground">
          <MapPin className="h-4 w-4 mr-2" /> Use My Location
        </Button>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto text-left">
          {[
            { title: "Search", desc: "Find drivers and companies by city, zip code, or your current location." },
            { title: "Compare", desc: "Compare rates, services, and reviews from multiple providers." },
            { title: "Contact", desc: "Reach out directly via phone, email, or website." },
          ].map((step, i) => (
            <div key={i} className="flex gap-4">
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">
                {i + 1}
              </div>
              <div>
                <h2 className="font-semibold mb-1">{step.title}</h2>
                <p className="text-sm text-muted-foreground">{step.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSearch;
